import React, { useState, useEffect, useRef } from "react";
import ReactModal from "react-modal";
import { useNavigate } from "react-router-dom";
import { getApi } from "../Services/apiService";
import { toast } from "react-toastify";
import { CirclePicker, ChromePicker } from "react-color";
import "../popups.css";
import namer from "color-namer";
import { sortByKey } from "../Utils/sortByKey";
import { formatNameWithSuffix } from "../Utils/name";
import {
  getFamilyTreeHeadLineRestriction,
  SETTINGS_SUBSCRIPTION_PATH,
  TREE_SIDE_API_FATHER,
  TREE_SIDE_API_MOTHER,
} from "../Utils/subscriptionAccess";

const predefinedColors = [
  { name: "Silver", hex: "#78706E" },
  { name: "Dk Green", hex: "#3C7D22" },
  { name: "Purple", hex: "#764b92" },
  { name: "Orange", hex: "#FF6400" },
  { name : "Coral", hex:"#ff8c55"},
  { name: "Med Blue", hex: "#01B0F1" },
  { name: "Red", hex: "#FE0000" },
  { name: "Gold", hex: "#D6A953" },
  { name: "Lime", hex: "#84B701" },
  { name: "Black", hex: "#080f1f" },
  { name: "Teal", hex: "#187a95" },
  { name: "Caramel", hex: "#d56718" },
  { name: "Magenta", hex: "#C4006E" },
  { name: "Dk Blue", hex: "#0E1667" },
  { name: "Navy Blue", hex: "#0050B3" },
  { name: "Sky Blue", hex: "#00D7FE" },
  { name: "Keely Green", hex: "#59A742" },
  { name: "Green", hex: "#84C664" },
  { name: "Mustard", hex: "#CB9800" },
  { name: "Olive", hex: "#D3C119" },
  { name: "Chocolate", hex: "#81431C" },
  { name: "Crimson", hex: "#B40001" },
  { name: "Blood Orange", hex: "#D84C3B" },
  { name: "Brown", hex: "#CF6E1F" },
  { name: "French Rose", hex: "#DC566E" },
  { name: "Sand", hex: "#CAB274" },
  { name: "Clay", hex: "#E8A561" },
  { name: "Salmon Pink", hex: "#FFA99F" },
  { name: "Burgundy", hex: "#79081C" },
  { name: "Marigold", hex: "#FF9C00" },
  { name: "Rose Tan", hex: "#C79089" }

];

function normalizeRootHeadDisplayLabel(text) {
  if (!text) return "";
  return String(text)
    .replace(/\bGrandFather\b/g, "Grandfather")
    .replace(/\bGrandMother\b/g, "Grandmother");
}

function rootHeadOptionDisplayName(option) {
  if (option?.label && option?.rootFamilyLineSide) {
    return normalizeRootHeadDisplayLabel(option.label);
  }
  const displayName = option.label || option.name || option.value || "";
  return normalizeRootHeadDisplayLabel(displayName);
}

/** Lowercase, collapse spaces so "Grand Father" matches grandfather detection. */
function optionNameNormalized(option) {
  return (option.name || option.label || "")
    .toLowerCase()
    .replace(/\s+/g, "");
}

/**
 * Root "Add first generation": two choices — GrandFather (Mother's/Father's side).
 * Excludes Grandmother. Pairs API rows by name hints when possible; otherwise duplicates a single GF row.
 */
function buildDualGrandfatherRootLineOptions(allFamilies) {
  const grandfatherRows = (allFamilies || []).filter((option) => {
    const n = optionNameNormalized(option);
    return n.includes("grandfather") && !n.includes("grandmother");
  });
  if (grandfatherRows.length === 0) return [];

  const pickByHint = (needle) =>
    grandfatherRows.find((o) => optionNameNormalized(o).includes(needle));

  let motherRow = pickByHint("mother");
  let fatherRow = pickByHint("father");

  if (!motherRow && !fatherRow && grandfatherRows.length >= 2) {
    motherRow = grandfatherRows[0];
    fatherRow = grandfatherRows[1];
  } else {
    if (!motherRow) motherRow = grandfatherRows[0];
    if (!fatherRow) {
      fatherRow =
        grandfatherRows.find((g) => (g.id ?? g.value) !== (motherRow.id ?? motherRow.value)) ||
        motherRow;
    }
  }

  const toSynthetic = (apiRow, side) => {
    const rawId = apiRow.id ?? apiRow.value;
    const sideKey = side === "mother" ? "mother" : "father";
    const label =
      sideKey === "mother"
        ? "Grandfather & Grandmother (Mother's Side)"
        : "Grandfather & Grandmother (Father's Side)";
    return {
      ...apiRow,
      id: rawId,
      value: `gf_rootline_${sideKey}_${rawId}`,
      label,
      name: apiRow.name || apiRow.label || "GrandFather",
      rootFamilyLineSide: sideKey,
    };
  };

  return [toSynthetic(motherRow, "mother"), toSynthetic(fatherRow, "father")];
}

// Set the app element for accessibility
if (process.env.NODE_ENV !== 'test') {
  ReactModal.setAppElement('#root');
}

const RootHeadModal = ({ isOpen, onClose, onNext, parentMember, rootHeadData = {},isEditMode=false,selectedProfileColor="",setSelectedProfileColor=()=>{},familyTreeId=null,allUsedColorsFromTree=[]}) => {
  const navigate = useNavigate();
  const [selectedColor, setSelectedColor] = useState("");
  const [selectedHead, setSelectedHead] = useState("");
  const [headOptions, setHeadOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [colorError, setColorError] = useState(false);
  const [dropdownError, setDropdownError] = useState(false);
  const [showCustomPicker, setShowCustomPicker] = useState(false);
  const [manualySelectedColor, setManualySelectedColor] = useState(false);
  const [headLineRestriction, setHeadLineRestriction] = useState(() =>
    getFamilyTreeHeadLineRestriction()
  );
  const [headMenuOpen, setHeadMenuOpen] = useState(false);

  const [validationAttempted, setValidationAttempted] = useState(false);
  const customPickerRef = useRef(null);
  const headMenuRef = useRef(null);
  const [allUsedColors, setAllUsedColors] = useState([]); // Store all used colors from entire family tree

  // Real tree parent has a stable member key; avoid treating {} / empty objects as "add child" (that forced the native GF/GM select).
  const parentMemberKey =
    parentMember?.id ??
    parentMember?.member_id ??
    parentMember?.related_member_id ??
    parentMember?.user_id;
  const isAddingChild = Boolean(
    parentMember &&
      parentMemberKey != null &&
      String(parentMemberKey).trim() !== ""
  );
  /** Custom listbox for root start: dual grandfather lines + plan-based disabled options. */
  const useCustomHeadLineDropdown = !isAddingChild && !isEditMode;

  useEffect(() => {
    const sync = () => setHeadLineRestriction(getFamilyTreeHeadLineRestriction());
    sync();
    window.addEventListener("loggedInUserRoleUpdated", sync);
    const onStorage = (e) => {
      if (e.key === "loggedIn_user_role" || e.key == null) sync();
    };
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("loggedInUserRoleUpdated", sync);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  useEffect(() => {
    if (!headMenuOpen) return;
    const onDoc = (e) => {
      if (headMenuRef.current && !headMenuRef.current.contains(e.target)) {
        setHeadMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [headMenuOpen]);

  // Get parent member's name for display
  const getParentMemberName = () => {
    if (!parentMember) return '';
    
    // Construct from parts (check both snake_case and camelCase) to ensure special name and suffix are included
    // Only show first name, special name/initial, and suffix (exclude middle name and last name)
    const firstName = parentMember.first_name || parentMember.firstName || '';
    const suffix = parentMember.suffix || '';
    const specialName = parentMember.special_name || parentMember.specialName || '';
    
    let parentName = '';
    
    // If we have at least a first name, construct from parts to include special name and suffix
    if (firstName || specialName || suffix) {
      // Build name parts array (only first name and special name)
      const nameParts = [];
      
      // Add first name
      if (firstName) {
        nameParts.push(firstName);
      }
      
      // Add special name (without dot) if it exists
      if (specialName) {
        nameParts.push(specialName);
      }
      
      // Join name parts
      const baseName = nameParts.join(' ');
      
      // Add suffix with comma and period if it exists
      if (suffix) {
        // Ensure suffix ends with a period (only add if not already present)
        const suffixWithPeriod = suffix.trim().endsWith('.') ? suffix.trim() : `${suffix.trim()}.`;
        parentName = baseName ? `${baseName}, ${suffixWithPeriod}` : suffixWithPeriod;
      } else {
        // Return base name or fallback to first name
        parentName = baseName || firstName || '';
      }
    } else {
      // Fallback: extract first name from full_name, fullName, or name if individual parts are not available
      if (parentMember.full_name) {
        // Extract first name from full_name (take first word)
        const firstWord = parentMember.full_name.split(' ')[0];
        parentName = firstWord || '';
      } else if (parentMember.fullName) {
        const firstWord = parentMember.fullName.split(' ')[0];
        parentName = firstWord || '';
      } else if (parentMember.name) {
        const firstWord = parentMember.name.split(' ')[0];
        parentName = firstWord || '';
      }
    }
    
    // For generation 1 (START/TOP OF THE LINE), also show spouse name
    const parentGeneration = parentMember.generation || parentMember.generation_level || 0;
    if (parentGeneration === 1) {
      // Get spouse name (check multiple possible field names)
      const spouseFirstName = parentMember.spouse_first_name || parentMember.spouseFirstName || '';
      const spouseFullName = parentMember.spouse_full_name || parentMember.spouseFullName || '';
      
      // Use spouse full name if available, otherwise use first name
      const spouseName = spouseFullName || spouseFirstName;
      
      if (spouseName) {
        // Format: "Parent Name & Spouse Name"
        return parentName ? `${parentName} & ${spouseName}` : spouseName;
      }
    }
    
    return parentName;
  };

  // console.log("parentMemeber in choose color modal : ",parentMember);

  let customColorName;

  // console.log("rootHeadData??????????" ,rootHeadData?.selectedColor);
  // console.log("parent member data : ",parentMember)
  // debugger
  // const isInherited = rootHeadData?.generation >= 2;
  const isInherited = isEditMode 
  ? rootHeadData?.generation >= 3  // In edit mode, inherit if generation >= 3
  : rootHeadData?.generation >= 2; // In add mode, inherit if generation >= 2
  // console.log("isInherited", rootHeadData?.generation);

  const inheritedColor = rootHeadData?.selectedColor;
  const handleNext = () => {
    console.log("handle next here.");
    setValidationAttempted(true);

    // Validate form
    const isDropdownValid = !!selectedHead;
    const isColorValid = parentMember?.generation == 1 ? !!selectedColor : true;
    // Check if color is used by ANY generation (not just children)
    const isColorUsed = (parentMember?.generation == 1 || isEditMode) && isColorAlreadyUsed();


    if(!isEditMode){
          setDropdownError(!isDropdownValid);
    }
    setColorError(!isColorValid || isColorUsed);

    // If any validation fails, stop here and show errors
    if ((!isDropdownValid || !isColorValid || isColorUsed) && !isEditMode ) {
      return;
    }
    // Find the selected head option to get the ID
    const selectedHeadOption = headOptions.find(
      (option) => String(option.value ?? option.id) === String(selectedHead)
    );

    const apiHeadId = selectedHeadOption?.id ?? selectedHeadOption?.value ?? null;
    const lineSide = selectedHeadOption?.rootFamilyLineSide;
    const tree_side =
      lineSide === "mother"
        ? TREE_SIDE_API_MOTHER
        : lineSide === "father"
          ? TREE_SIDE_API_FATHER
          : null;

    const rootHeadData = {
      selectedHead: selectedHead,
      selectedHeadId: apiHeadId,
      selectedHeadName: selectedHeadOption?.name || selectedHeadOption?.label || null,
      rootFamilyLineSide: lineSide || null,
      tree_side,
      selectedColor: selectedColor,
      colorName: getColorName(),
    };

    if (onNext) onNext(rootHeadData);
    onClose();
    setValidationAttempted(false);

  };

  // Helper function to check if color is already used (globally across all generations)
  const isColorAlreadyUsed = () => {
    if (!selectedColor) return false;
    
    // Normalize the selected color for comparison
    const normalizedSelected = selectedColor.toLowerCase().trim();
    
    // In edit mode, get the current member's color to exclude it from the check
    const currentMemberColor = isEditMode && rootHeadData?.selectedColor 
      ? rootHeadData.selectedColor.toLowerCase().trim() 
      : null;
    
    // Check against all used colors from entire family tree (excluding current member's color in edit mode)
    const isUsed = allUsedColors.some(usedColor => {
      const normalizedUsed = (usedColor || '').toLowerCase().trim();
      // Skip if this is the current member's color in edit mode
      if (isEditMode && normalizedUsed === currentMemberColor) {
        return false;
      }
      return normalizedUsed === normalizedSelected;
    });
    
    // Also check parent's children colors for backward compatibility
    if (parentMember?.childrenColors?.length && parentMember?.generation === 1) {
      const isUsedByChildren = parentMember.childrenColors.some(
        childColor => {
          const normalizedChildColor = (childColor.colorCode || '').toLowerCase().trim();
          // Skip if this is the current member's color in edit mode
          if (isEditMode && normalizedChildColor === currentMemberColor) {
            return false;
          }
          return normalizedChildColor === normalizedSelected;
        }
      );
      return isUsed || isUsedByChildren;
    }
    
    return isUsed;
  };

  useEffect(() => {
    if (isOpen) {
      // Reset to empty string when modal opens
      setSelectedColor("");
      setManualySelectedColor(false);
      setManualySelectedColor(false);
      setValidationAttempted(false);
      setDropdownError(false);
      setColorError(false);

      setSelectedHead("");
      setHeadMenuOpen(false);
    }
  }, [isOpen]);

useEffect(() => {
  if (!showCustomPicker) return;

  const handlePointerDown = (event) => {
    const path = event.composedPath ? event.composedPath() : [];

    // If clicking inside the picker → DO NOT CLOSE
    if (customPickerRef.current && path.includes(customPickerRef.current)) {
      return;
    }

    // If clicking the custom button → DO NOT CLOSE
    const customButton = document.querySelector('.color-option.custom-color');
    if (customButton && path.includes(customButton)) {
      return;
    }

    // OTHERWISE CLOSE
    setShowCustomPicker(false);
  };

  document.addEventListener("pointerdown", handlePointerDown);

  return () => {
    document.removeEventListener("pointerdown", handlePointerDown);
  };
}, [showCustomPicker]);



  // Update error states when values change (after validation was attempted)
  useEffect(() => {
    if (validationAttempted) {
      const isDropdownValid = !!selectedHead;
      const isColorValid = parentMember?.generation == 1 ? !!selectedColor : true;
      const isColorUsed = parentMember?.generation == 1 && isColorAlreadyUsed();

      setDropdownError(!isDropdownValid);
      setColorError(!isColorValid || isColorUsed);
    }
  }, [selectedHead, selectedColor, validationAttempted, parentMember, allUsedColors]);

  // Update allUsedColors from prop, excluding current member's color in edit mode
  useEffect(() => {
    if (!isOpen) return;
    
    // In edit mode, get the current member's color to exclude it
    const currentMemberColor = isEditMode && rootHeadData?.selectedColor 
      ? rootHeadData.selectedColor.toLowerCase().trim() 
      : null;
    
    // Filter out current member's color in edit mode
    const filteredColors = currentMemberColor
      ? allUsedColorsFromTree.filter(color => {
          const normalizedColor = (color || '').toLowerCase().trim();
          return normalizedColor !== currentMemberColor;
        })
      : allUsedColorsFromTree;
    
    setAllUsedColors(filteredColors);
    console.log('All used colors from family tree (excluding current member in edit mode):', filteredColors);
  }, [isOpen, allUsedColorsFromTree, isEditMode, rootHeadData?.selectedColor]);

  // Fetch root head family data from API
  useEffect(() => {
    const fetchRootHeadFamily = async () => {
      if (!isOpen) return;

      setLoading(true);
      setError(null);

      // console.log("useState here ==================== ",selectedColor)
      // //set color other than black as default here
      // setSelectedColor("#000000")

      try {
        const response = await getApi('/root-head-family');

        if (response?.statusCode === 200) {
          console.log("response", response.data);

          const allFamilies = response?.data?.families || [];

          // Filter options based on generation level
          let filteredOptions = [];

          if (!isAddingChild) {
            filteredOptions = buildDualGrandfatherRootLineOptions(allFamilies);
            console.log("Showing root options (dual GrandFather lines):", filteredOptions);
          } else {
            // Adding child (Generation 2+): Show Son, Daughter, etc.
            filteredOptions = allFamilies.filter(option => {
              const name = (option.name || option.label || '').toLowerCase();
              return name.includes('son') || name.includes('daughter');
            });
            console.log('Showing generation 2 options (Son/Daughter):', filteredOptions);
          }

          // Keep mother/father root order; sort child relations only
          const sortedOptions =
            isAddingChild && filteredOptions.length > 0
              ? sortByKey(
                  filteredOptions,
                  filteredOptions[0].name ? "name" : "label"
                )
              : filteredOptions;
          setHeadOptions(sortedOptions);
        } else {
          toast.error(response?.message || 'Failed to fetch root head family data');
          // Fallback to default options if API fails
        }
      } catch (error) {
        console.error("Error fetching root head family:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchRootHeadFamily();
  }, [isOpen, isAddingChild, parentMember]);

  // === Prefill & Lock Logic ===
  // === Prefill & Lock Logic ===
  useEffect(() => {
    if (!isOpen) return;

    const parentGen = parentMember?.generation || 1;
    console.log("parentGen", parentGen);

    const prefillColor = rootHeadData?.selectedColor;

    // If root head (Gen 1) → allow color selection
    if (parentGen === 1) {
      // setSelectedColor("#000000");
      setShowCustomPicker(false);

    } else {
      // For Gen 2 and beyond → inherit Gen 2 color and disable selection
      // const inheritedColor = rootHeadData?.selectedColor || "#000000";
      const inheritedColor = rootHeadData?.selectedColor;
      setSelectedColor(inheritedColor);
      setShowCustomPicker(false);
    }

    setSelectedHead("");
  }, [isOpen, rootHeadData, parentMember]);

  useEffect(() => {
    if (NextDisabled() && selectedColor && rootHeadData?.generation == 1) {
      setColorError(true);
    } else {
      setColorError(false);
    }
  }, [selectedColor, parentMember, isInherited, familyTreeId, allUsedColors]);




  // Get current color name
  const getColorName = () => {
    // const foundColor = predefinedColors?.find((c) => c.hex.toLowerCase() === selectedColor.toLowerCase());
    const normalizedSelected = typeof selectedColor === 'string' ? selectedColor.toLowerCase() : '';
 const foundColor = normalizedSelected ? predefinedColors?.find((c) => c.hex.toLowerCase() === normalizedSelected) : null;
    if (selectedColor && !foundColor) {

      customColorName = namer(selectedColor.toLowerCase()) || "custom color";
      console.log("customColorName", customColorName.html[0].name);
      return customColorName.html[0].name;
    }
    return foundColor ? foundColor.name : customColorName;
  };

  const NextDisabled = () => {
    // console.log("next disbaled here")
    // Check if the selected color is already used globally (by any generation)
    const isColorUsedGlobally = isColorAlreadyUsed();

    const isFormIncomplete = !selectedHead ||
      (!selectedColor && !manualySelectedColor && parentMember?.generation === 1);

    const formDisabled = isColorUsedGlobally && (rootHeadData?.generation == 1 || isEditMode);
    return formDisabled;
  };

  const selectedHeadOptionForLabel = headOptions.find(
    (o) => String(o.value ?? o.id) === String(selectedHead)
  );
  const selectedHeadDisplayLabel = selectedHeadOptionForLabel
    ? rootHeadOptionDisplayName(selectedHeadOptionForLabel)
    : "";

  const goSubscriptionSettings = (e) => {
    e?.stopPropagation?.();
    e?.preventDefault?.();
    setHeadMenuOpen(false);
    onClose();
    navigate(SETTINGS_SUBSCRIPTION_PATH);
  };

  const chevronSvg = (
    <svg
      style={{
        position: "absolute",
        right: "12px",
        top: "50%",
        transform: "translateY(-50%)",
        pointerEvents: "none",
        width: "30px",
        height: "30px",
        fill: "#666",
      }}
      viewBox="0 0 20 20"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" />
    </svg>
  );

  return (
    <ReactModal
      isOpen={isOpen}
      onRequestClose={onClose}
      className="popup-card roothead-card"
      overlayClassName="popup-overlay roothead-overlay"
      closeTimeoutMS={200}
    >
      {/* ===== Header ===== */}
      <div className="popup-header roothead-header">
        <h2>
        {
          isEditMode 
            ? "Update Family Line/Color" 
            : isAddingChild 
              ? `Add Family Member to ${getParentMemberName() || 'Family Member'}`
              : "Set Start/Top of the family tree"
        }
        </h2>
        <button className="close-btn" onClick={onClose} aria-label="Close">
          &times;
        </button>
      </div>

      {/* ===== Description ===== */}
      {isEditMode && (
        <p className="popup-desc roothead-desc">
          Update the Family Line color for this head. This change will apply to all descendants in the line.
        </p>
      )}

      {/* ===== Dropdown ===== */}
      {
        !isEditMode && (
          <div className="form-group roothead-group">
            <label htmlFor="headOfFamily">
              {isAddingChild
                ? "Relation to Associated Family Member"
                : "Select Names for Top of the Line"}
            </label>
            <div
              className={
                useCustomHeadLineDropdown ? "roothead-head-dropdown-shell" : undefined
              }
              style={{ position: "relative" }}
              ref={useCustomHeadLineDropdown ? headMenuRef : undefined}
            >
            {useCustomHeadLineDropdown ? (
              <>
                <button
                  type="button"
                  id="headOfFamily"
                  className={`dropdown roothead-dropdown form-control roothead-head-dropdown-toggle ${!selectedHead ? "placeholder-bold" : ""}`}
                  disabled={loading}
                  aria-expanded={headMenuOpen}
                  aria-haspopup="listbox"
                  onClick={() => !loading && setHeadMenuOpen((open) => !open)}
                  style={{
                    width: "100%",
                    textAlign: "left",
                    cursor: loading ? "not-allowed" : "pointer",
                    appearance: "none",
                    WebkitAppearance: "none",
                    MozAppearance: "none",
                    paddingRight: "36px",
                  }}
                >
                  {loading
                    ? "Loading..."
                    : selectedHeadDisplayLabel || "Choose Head of Family"}
                </button>
                {chevronSvg}
                {headMenuOpen && !loading && (
                  <ul
                    className="roothead-head-dropdown-list roothead-head-dropdown-list--restricted"
                    role="listbox"
                    id="head-of-family-listbox"
                  >
                    {headOptions.map((option) => {
                      const val = String(option.value ?? option.id ?? "");
                      const label = rootHeadOptionDisplayName(option);
                      const locked =
                        (headLineRestriction === "mother_only" &&
                          option.rootFamilyLineSide === "father") ||
                        (headLineRestriction === "father_only" &&
                          option.rootFamilyLineSide === "mother");
                      const isSelected = String(selectedHead) === val;
                      if (locked) {
                        return (
                          <li
                            key={val}
                            className="roothead-head-dropdown-item--disabled-wrap"
                          >
                            <div
                              className="roothead-head-dropdown-item is-disabled"
                              aria-disabled="true"
                            >
                              {label}
                            </div>
                            <div
                              className="roothead-head-plan-hint-popover"
                              onMouseDown={(e) => e.stopPropagation()}
                            >
                              <p className="roothead-head-plan-hint-text">
                                <>
                                  Your current plan allows you to manage a single family line.{" "}
                                  Upgrade your subscription to add another family line.
                                </>
                              </p>
                              <button
                                type="button"
                                className="btn-primary roothead-hint-upgrade-btn"
                                onClick={goSubscriptionSettings}
                              >
                                Subscription settings
                              </button>
                            </div>
                          </li>
                        );
                      }
                      return (
                        <li
                          key={val}
                          role="option"
                          aria-selected={isSelected}
                          className={`roothead-head-dropdown-item ${isSelected ? "is-selected" : ""}`}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => {
                            setSelectedHead(val);
                            setHeadMenuOpen(false);
                            setDropdownError(false);
                          }}
                        >
                          {label}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </>
            ) : (
              <>
            <select
              id="headOfFamily"
              className={`dropdown roothead-dropdown form-control ${!selectedHead ? 'placeholder-bold' : ''}`}
              value={selectedHead}
              onChange={(e) => setSelectedHead(e.target.value)}
              disabled={loading}
                style={{ 
                  appearance: 'none',
                  WebkitAppearance: 'none',
                  MozAppearance: 'none',
                  paddingRight: '30px',
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right center',
                  backgroundSize: '20px'
                }}
            >
              <option value="" style={{ fontWeight: 'bold' }}>
                {loading ? "Loading..." : (isAddingChild ? "Choose Relation" : "Choose Head of Family")}
              </option>
                {headOptions.map((option) => {
                  const displayName = rootHeadOptionDisplayName(option);
                  return (
                <option key={option.value || option.id} value={option.value || option.id}>
                      {displayName}
                </option>
                  );
                })}
            </select>
              {chevronSvg}
              </>
            )}
            </div>
            {error && (
              <p className="error-message">
                Error loading data: {error}
              </p>)}
          </div>
        )
      }

      {/* ===== Color Selection ===== */}
      <div className="form-group roothead-group">
        <label>
          {isAddingChild ? (
            <>Head of Family Line Color: <span style={{ color: selectedColor, fontWeight: 'bold' }}>{getColorName()}</span></>
          ) : (
            <>
              {isEditMode ? "Assign Family Line/Color":"Start/Top of Family Line Color"}
              {selectedColor && (
                <>:{" "}
              <span className="selected-color-display" style={{ color: selectedColor }}>
                {getColorName()} ({selectedColor})
              </span>
                </>
              )}
            </>
          )}
        </label>

        <div className="color-picker-wrapper" style={{
          pointerEvents: isInherited ? "none" : "auto",
          opacity: isInherited ? 0.5 : 1,
          cursor: isInherited ? "not-allowed" : "default",
        }}>
          <div className="color-grid">
            {predefinedColors.map((color) => {
              const normalizedColorHex = color.hex.toLowerCase().trim();
              // In edit mode, get the current member's color to exclude it
              const currentMemberColor = isEditMode && rootHeadData?.selectedColor 
                ? rootHeadData.selectedColor.toLowerCase().trim() 
                : null;
              
              // Check if this color is used by ANY generation (excluding current member's color in edit mode)
              const isColorUsedGlobally = allUsedColors.some(usedColor => {
                const normalizedUsed = (usedColor || '').toLowerCase().trim();
                // Skip if this is the current member's color in edit mode
                if (isEditMode && normalizedUsed === currentMemberColor) {
                  return false;
                }
                return normalizedUsed === normalizedColorHex;
              });
              
              // Also check parent's children colors for backward compatibility
              const isColorUsedByChildren = parentMember?.childrenColors?.map((data) => {
                const normalizedChildColor = (data.colorCode || '').toLowerCase().trim();
                // Skip if this is the current member's color in edit mode
                if (isEditMode && normalizedChildColor === currentMemberColor) {
                  return false;
                }
                return normalizedChildColor === normalizedColorHex;
              }).includes(true);
              
              const isDisabled = isInherited || isColorUsedGlobally || isColorUsedByChildren;
              
              return (
              <div
                key={color.hex}
                className={`color-option ${isDisabled ? "disabled" : ""}`}
                onClick={() => {
                  if (isInherited) return; // prevent color change
                  if (isColorUsedGlobally || isColorUsedByChildren) return;
                  setSelectedColor(color.hex);
                  setManualySelectedColor(true);
                  setShowCustomPicker(false);
                }}
              >
                <div
                  className={`color-circle-wrapper ${(selectedColor?.toLowerCase() || "" ) === color.hex.toLowerCase() ? "selected" : ""
                    }`}
                  style={{
                    backgroundColor: color.hex,
                    cursor: isInherited ? "not-allowed" : "pointer",
                  }}
                />
                <div className="color-name-label">{color.name}</div>
                <div className="color-hex-code">({color.hex})</div>
                {/* <div className="tooltip">Each color can be used only once.</div> */}
                {
                  (parentMember?.generation == 1 || isEditMode) && (
                    <div className="tooltip">Each color can only be used once.</div>
                  )
                }
              </div>
              );
            })}

            {/* Custom Color Button */}
            {/* <div
              className={`color-option custom-color ${isInherited ? "disabled" : ""}`}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                if (isInherited) return;
                setShowCustomPicker(!showCustomPicker);
              }}
            >
              <div
                className={`custom-color-circle ${showCustomPicker && !isInherited ? "selected" : ""
                  }`}
              >
                +
              </div>
              <div className="color-name-label">Custom</div>
              
              {showCustomPicker && !isInherited && (
                <div 
                  ref={customPickerRef}
                  className="chrome-picker-container"
                  onPointerDown={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                >
                  <ChromePicker
                    color={selectedColor}
                    onChange={(color) => {
                      const newColorHex = color.hex;
                      // Check if the custom color is already used
                      const normalizedNewColor = newColorHex.toLowerCase().trim();
                      
                      // In edit mode, get the current member's color to exclude it
                      const currentMemberColor = isEditMode && rootHeadData?.selectedColor 
                        ? rootHeadData.selectedColor.toLowerCase().trim() 
                        : null;
                      
                      // Check if custom color is used (excluding current member's color in edit mode)
                      const isCustomColorUsed = allUsedColors.some(usedColor => {
                        const normalizedUsed = (usedColor || '').toLowerCase().trim();
                        // Skip if this is the current member's color in edit mode
                        if (isEditMode && normalizedUsed === currentMemberColor) {
                          return false;
                        }
                        return normalizedUsed === normalizedNewColor;
                      });
                      
                      if (!isCustomColorUsed) {
                        setSelectedColor(newColorHex);
                        setManualySelectedColor(true);
                        setColorError(false);
                      } else {
                        setColorError(true);
                        toast.warning("This color is already used by another family member. Please choose a different color.");
                      }
                    }}
                    disableAlpha={false}
                  />
                </div>
              )}

            </div> */}
          </div>


          {/* {
            colorError && (
              <div className="roothead-modal-error">
                <p>Each color can be used only once.</p>
              </div>
            )
          } */}
          {
            dropdownError && (
              <div className="roothead-modal-error">
                <p>⚠️ Please select a relation from the dropdown.</p>
              </div>
            )
          }
          {/* Color Error Messages */}
          {colorError && validationAttempted && (
            <div className="roothead-modal-error">
              {!selectedColor ? (
                <p>⚠️ Please select a color for the family line</p>
              ) : (
                <p>⚠️ This color is already used by another family member. Please select a different color.</p>
              )}
            </div>
          )}



          {/* Custom Picker only if not inherited */}
          {/* {showCustomPicker && !isInherited && (
    <div className="chrome-picker-container">
      <ChromePicker
        color={selectedColor}
        onChange={(color) => {
          setSelectedColor(color.hex);
          setManualySelectedColor(true);
        }}
        disableAlpha={false}
      />
    </div>
  )} */}

          {/* Optional: Info label for inherited color */}
          {isInherited && (
            <div className="inherited-color-info">
              <small style={{ opacity: 0.8 }}>
                Inherited color: <strong style={{ color: inheritedColor }}>{rootHeadData?.inheritedColor}</strong>
              </small>
            </div>
          )}
        </div>

      </div>

      {/* ===== Buttons ===== */}
      <div className="popup-actions roothead-actions">
        <button className="btn-outline" onClick={onClose}>
          Cancel
        </button>
        <button
          className="btn-primary"
          onClick={handleNext}
        // disabled={!selectedHead || (!selectedColor && !manualySelectedColor && parentMember?.generation == 1) || (NextDisabled())}
        >
          Next
        </button>
      </div>
    </ReactModal>
  );
};

export default RootHeadModal;